import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import JWTDecode from 'jwt-decode';

import { TRolesActions } from '../../features/roles/store/types/roles.types';
import { setCurrentSupportUser } from '../../features/dashboard-users/store/support-user.actions';

import { AuthService } from '@/services/AuthService';

import { store } from '@/store/configuration';
import { TAppState } from '@/store/reducers/reducers';
import { advancedRoleStatus } from '@/features/roles/store/models/advancedRole.model';
import { IAccessToken, IAccessTokenDecoded } from '@/store/models/auth.model';

import { TAuthAction, AuthActionTypes, IAuthState } from '@/store/types/auth.types';
import { TAppError } from '@/store/types/base.state';
import { SupportUserService } from '@/features/dashboard-users/services/SupportUserService';
import { TSupportUserAction } from '@/features/dashboard-users/store/types/support-user.types';
import { decode_token } from '@/features/dashboard-users/helpers/decode_token';
import { getAdvandeRoleFromScopes } from '@/hooks/useAccessControl';
import { setAdvancedRole } from '@/features/roles/store/actions/advancedRole.actions';

export const fetchAccessToken = (
  authorizationCode: string,
): ThunkAction<Promise<void>, IAuthState, {}, TAuthAction> => {
  const request = (): TAuthAction => ({
    type: AuthActionTypes.FETCH_ACCESS_TOKEN,
  });
  const fail = (error: TAppError): TAuthAction => ({
    type: AuthActionTypes.FETCH_ACCESS_TOKEN_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/authentication.html#authenticating-is-a-two-step-process',
  });
  const success = (token: IAccessToken): TAuthAction => ({
    type: AuthActionTypes.FETCH_ACCESS_TOKEN_SUCCESS,
    shouldSkipToaster: true,
    token,
  });

  return async (dispatch: ThunkDispatch<IAuthState, {}, TAuthAction>): Promise<void> => {
    dispatch(request());
    try {
      const token = await AuthService.getAccessToken(authorizationCode);

      dispatch(success(token));
      const accessTokenDecoded = JWTDecode(token.access_token);
      if (!accessTokenDecoded) return Promise.reject('Error on decoding credentials');

      dispatch(success(token));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setAccessToken = (token: IAccessToken): TAuthAction => ({
  type: AuthActionTypes.FETCH_ACCESS_TOKEN_SUCCESS,
  shouldSkipToaster: true,
  token,
});

export const logout = (): TAuthAction => {
  return {
    type: AuthActionTypes.AUTH_LOGOUT,
  };
};

export const refreshAccessToken = () => {
  const request = (): TAuthAction => ({
    type: AuthActionTypes.REFRESH_TOKEN,
  });
  const fail = (error: TAppError): TAuthAction => ({
    type: AuthActionTypes.REFRESH_TOKEN_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/authentication.html#refreshing-the-token',
  });
  const success = (accessToken: IAccessToken): TAuthAction => ({
    type: AuthActionTypes.REFRESH_TOKEN_SUCCESS,
    accessToken,
  });

  return async (
    dispatch: ThunkDispatch<IAuthState | TAppState, {}, TAuthAction | TRolesActions | TSupportUserAction>,
  ): Promise<void> => {
    dispatch(request());
    try {
      const authState: IAuthState = store.getState()?.auth;
      const refreshToken = authState.token?.refresh_token;
      const credentials = authState?.auth2Fa?.credentials;
      if (!refreshToken) {
        dispatch(fail('No refresh token found!'));
        return;
      }

      const token = await AuthService.refreshAccessToken(refreshToken, credentials);

      const accessTokenDecoded: IAccessTokenDecoded | undefined = decode_token(token.access_token);
      const scopes = accessTokenDecoded?.scope;

      dispatch(success(token));
      const supportUserId = authState?.currentSupportUser?.state?.id;
      const newSupportUser = await SupportUserService.searchSupportUser(supportUserId);
      if (newSupportUser) {
        dispatch(setCurrentSupportUser({ ...newSupportUser, metadata: newSupportUser.metadata || {} }));
        if (!newSupportUser.metadata?.role) {
          const assumeAdvancedRole = getAdvandeRoleFromScopes(scopes);
          dispatch(setAdvancedRole(assumeAdvancedRole.slug, advancedRoleStatus.PENDING, newSupportUser.id));
        }
      }
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
